<template>
  <div class="container-fluid">
    <div class="loading" v-if="isLoading">Loading&hellip;</div>
    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Transportes</h1>
          <p class="view-header__subtitle">Administrá los transportes para asociarlos a clientes y proveedores.</p>
        </div>
      </div>
    </div>

    <!-- FILTROS -->
    <div class="filters-panel">
      <div class="filters-panel__section">
        <div class="filters-panel__section-title">
          <i class="bi bi-search"></i>
          Buscador
        </div>
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <input v-model="doSearch" type="search" class="form-control" placeholder="Buscar transporte...">
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- TABLA -->
      <div class="col-md-8">
        <div class="data-table">
          <vdtnet-table
            ref="table"
            :fields="fields"
            :opts="opts"
            :selectCheckbox="0"
            container-class-name="table-responsive"
            class-name="table mb-0"
            @edit="getTransport"
            @delete="deleteTransport"
          />
        </div>
      </div>

      <!-- FORMULARIO -->
      <div class="col-md-4">
        <div class="form-card">
          <div class="form-card__header">
            <h6 class="form-card__title">
              <i :class="transport.id == null ? 'bi bi-plus-lg' : 'bi bi-pencil'"></i>
              {{ transport.id == null ? 'Nuevo transporte' : 'Editar transporte' }}
            </h6>
            <button v-if="transport.id != null" type="button" class="form-card__btn" @click="resetForm()">
              <i class="bi bi-plus-lg"></i>
              Nuevo
            </button>
          </div>
          <div class="form-card__body">
            <form @submit.prevent>
              <div class="form-card__field">
                <label class="form-card__label" for="transport-name">Nombre</label>
                <input
                  id="transport-name"
                  v-model.trim="transport.name"
                  type="text"
                  class="form-control"
                  :class="{ 'is-invalid': nameState === false }"
                  placeholder="Nombre del transporte"
                >
                <div v-if="nameState === false" class="form-card__error">
                  El nombre debe tener un mínimo de 3 letras
                </div>
                <div v-else class="form-card__hint">Campo obligatorio</div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="form-card__field">
                    <label class="form-card__label" for="transport-address">Dirección</label>
                    <input id="transport-address" v-model.trim="transport.address" type="text" class="form-control" placeholder="Dirección">
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-card__field">
                    <label class="form-card__label" for="transport-postalcode">Código postal</label>
                    <input id="transport-postalcode" v-model.trim="transport.postalcode" type="text" class="form-control" placeholder="Código postal">
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="form-card__field">
                    <label class="form-card__label" for="transport-city">Localidad</label>
                    <input id="transport-city" v-model.trim="transport.city" type="text" class="form-control" placeholder="Localidad">
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-card__field">
                    <label class="form-card__label" for="transport-province">Provincia</label>
                    <q-select v-model="transport.province" :options="provinceOptions" placeholder="- Seleccionar -" />
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="form-card__field">
                    <label class="form-card__label" for="transport-phone1">Teléfono 1</label>
                    <input id="transport-phone1" v-model.trim="transport.phone_1" type="text" class="form-control" placeholder="Teléfono 1">
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-card__field">
                    <label class="form-card__label" for="transport-phone2">Teléfono 2</label>
                    <input id="transport-phone2" v-model.trim="transport.phone_2" type="text" class="form-control" placeholder="Teléfono 2">
                  </div>
                </div>
              </div>

              <div class="form-card__field">
                <label class="form-card__label" for="transport-routes">Destinos</label>
                <textarea id="transport-routes" v-model="transport.routes" class="form-control" rows="3"></textarea>
              </div>

              <div class="form-card__field">
                <label class="form-card__label" for="transport-notes">Observaciones</label>
                <textarea id="transport-notes" v-model="transport.notes" class="form-control" rows="3"></textarea>
              </div>
            </form>
          </div>
          <div class="form-card__footer">
            <button
              type="button"
              class="form-card__btn form-card__btn--primary"
              :disabled="!canSave || isLoading"
              @click="saveTransport()"
            >
              <i class="bi bi-check-lg"></i>
              Guardar transporte
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VdtnetTable from '@/components/global/VdtnetTable.vue';
import QSelect from '@/components/shared/QSelect.vue';

const PROVINCES = [
  { value: 'Capital Federal', text: 'CABA' },
  { value: 'Buenos Aires', text: 'Buenos Aires' },
  { value: 'Catamarca', text: 'Catamarca' },
  { value: 'Chaco', text: 'Chaco' },
  { value: 'Chubut', text: 'Chubut' },
  { value: 'Corrientes', text: 'Corrientes' },
  { value: 'Cordoba', text: 'Córdoba' },
  { value: 'Entre Rios', text: 'Entre Ríos' },
  { value: 'Formosa', text: 'Formosa' },
  { value: 'Jujuy', text: 'Jujuy' },
  { value: 'La Pampa', text: 'La Pampa' },
  { value: 'La Rioja', text: 'La Rioja' },
  { value: 'Mendoza', text: 'Mendoza' },
  { value: 'Misiones', text: 'Misiones' },
  { value: 'Neuquen', text: 'Neuquen' },
  { value: 'Rio Negro', text: 'Río Negro' },
  { value: 'Salta', text: 'Salta' },
  { value: 'San Juan', text: 'San Juan' },
  { value: 'San Luis', text: 'San Luis' },
  { value: 'Santa Cruz', text: 'Santa Cruz' },
  { value: 'Santa Fe', text: 'Santa Fe' },
  { value: 'Santiago del Estero', text: 'Santiago del Estero' },
  { value: 'Tierra del Fuego', text: 'Tierra del Fuego' },
  { value: 'Tucuman', text: 'Tucumán' },
];

const emptyTransport = () => ({
  id: null,
  name: "",
  address: "",
  phone_1: "",
  phone_2: "",
  postalcode: "",
  city: "",
  province: null,
  routes: "",
  notes: "",
});

export default {
  components: {
    VdtnetTable,
    QSelect,
  },
  data: function () {
    return {
      doSearch: '',
      isLoading: false,
      transport: emptyTransport(),
      provinces: PROVINCES,
      opts: {
        language: {
          url: '//cdn.datatables.net/plug-ins/1.13.4/i18n/es-ES.json',
        },
        ajax: {
          url: process.env.VUE_APP_BASE_URL + "/api/transports/list",
          type: "GET",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        },
        processing: false,
        pageLength: 15,
        searching: true,
        searchDelay: 100,
        destroy: true,
        ordering: true,
        lengthChange: true,
        serverSide: true,
        fixedHeader: true,
        autoWidth: false,
      },
      fields: {
        id: { visible: false },
        name: {
          label: "Nombre",
          searchable: true,
          render: (data, type) =>
            type === 'display' ? `<span class="data-table__primary">${data}</span>` : data,
        },
        address: { label: "Dirección" },
        phone_1: { label: "Teléfono 1", width: "130px" },
        phone_2: { label: "Teléfono 2", width: "130px" },
        postalcode: { visible: false },
        city: { visible: false },
        province: { visible: false },
        routes: { visible: false },
        notes: { visible: false },
        actions: {
          isLocal: true,
          label: "Acciones",
          width: "100px",
          align: "right",
          defaultContent: `
            <div class="table-action-group">
              <button data-action="edit" class="table-action-btn" type="button" title="Editar">
                <i class="bi bi-pencil"></i>
              </button>
              <button data-action="delete" class="table-action-btn" type="button" title="Eliminar">
                <i class="bi bi-trash"></i>
              </button>
            </div>
          `,
        },
      },
    };
  },
  computed: {
    provinceOptions() {
      return this.provinces.map(prov => ({ value: prov.value, label: prov.text }));
    },
    nameState() {
      const len = this.transport.name ? this.transport.name.length : 0;
      if (len === 0) return null;
      return len >= 3 ? null : false;
    },
    canSave() {
      return !!(this.transport.name && this.transport.name.trim().length >= 3);
    },
  },
  created() {
    this.$store.dispatch('transports/getTransports');
  },
  mounted() {
    this.$nextTick(() => {
      const dt = this.$refs.table && this.$refs.table.dataTable;
      if (!dt) return;
      dt.on('preXhr.dt', () => { this.isLoading = true; });
      dt.on('xhr.dt', () => { this.isLoading = false; });
    });
  },
  watch: {
    doSearch(newValue, oldValue) {
      if (newValue.length >= 4 || oldValue.length >= 4) {
        this.$refs.table.search(this.doSearch);
      }
      if (newValue === '' && oldValue.length > 0) {
        this.$refs.table.search(this.doSearch);
      }
    },
  },
  methods: {
    refreshTable() {
      if (this.$refs.table !== undefined) this.$refs.table.reload();
    },
    resetForm() {
      this.transport = emptyTransport();
    },
    getTransport(data) {
      this.$http.get("/api/transports/getTransport/" + data.id).then((response) => {
        this.transport = response.data.data;
      });
    },
    saveTransport() {
      if (!this.canSave || this.isLoading) return;
      const isEdit = this.transport.id != null;
      const url = isEdit
        ? "/api/transports/edit/" + this.transport.id
        : "/api/transports/add";
      this.$http.post(url, this.transport).then(() => {
        this.refreshTable();
        this.resetForm();
        this.$store.dispatch('transports/getTransports');
      });
    },
    deleteTransport(data) {
      if (!confirm("¿Está seguro que desea borrar este transporte?")) return;
      this.$http.post("/api/transports/delete", { id: data.id }).then((response) => {
        if (response.data.code === 201) {
          this.refreshTable();
          if (this.transport.id === data.id) this.resetForm();
          this.$store.dispatch('transports/getTransports');
        }
      });
    },
  },
};
</script>
